import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined'
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { eventImages, splashImage } from './eventUtils'
import { fadeInAnimation } from '../../utils/cssFunctions.js'

export default function EventHeader ({ eventData, status = { label: '', color: 'default' }, onShare }) {
  const { t } = useTranslation('events')
  const images = eventImages(eventData)
  const hero = splashImage(eventData)
  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)

  const count = images.length

  const openViewer = (i) => {
    setIndex(i)
    setOpen(true)
  }
  const showPrev = () => setIndex(prev => (prev - 1 + count) % count)
  const showNext = () => setIndex(prev => (prev + 1) % count)

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') showPrev()
      else if (e.key === 'ArrowRight') showNext()
      else if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, count])

  return (
    <Box
      component='header'
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: { xs: 480, md: 640 },
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden',
        bgcolor: '#0a0a0a'
      }}
    >
      {hero && (
        <Box
          component='img'
          src={hero}
          alt={eventData?.title || ''}
          fetchPriority='high'
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            ...fadeInAnimation
          }}
        />
      )}

      {/* Readability overlays */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.1) 32%, rgba(0,0,0,0.72) 72%, rgba(0,0,0,0.95) 100%)'
        }}
      />

      <IconButton
        onClick={onShare}
        aria-label={t('share')}
        sx={{
          position: 'absolute',
          top: 88,
          right: { xs: 16, md: 28 },
          zIndex: 3,
          color: 'white',
          bgcolor: 'rgba(255,255,255,0.12)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.2)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' }
        }}
      >
        <ShareOutlinedIcon />
      </IconButton>

      <Container maxWidth='lg' sx={{ position: 'relative', zIndex: 2, pt: { xs: 12, md: 18 }, pb: { xs: 4, md: 6 } }}>
        <Button
          component={Link}
          to='/events'
          size='small'
          startIcon={<ArrowBackIcon fontSize='small' />}
          sx={{
            color: 'rgba(255,255,255,0.85)',
            textTransform: 'none',
            fontWeight: 600,
            mb: 2.5,
            px: 1,
            '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' }
          }}
        >
          {t('backToEvents')}
        </Button>

        <Box sx={{ mb: 2 }}>
          <Chip
            label={status.label}
            color={status.color}
            size='small'
            sx={{
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              fontSize: '0.7rem',
              bgcolor: status.color === 'default' ? 'rgba(255,255,255,0.14)' : undefined,
              color: status.color === 'default' ? 'rgba(255,255,255,0.9)' : undefined,
              border: status.color === 'default' ? '1px solid rgba(255,255,255,0.2)' : 'none',
              backdropFilter: 'blur(8px)'
            }}
          />
        </Box>

        <Typography
          component='h1'
          sx={{
            color: 'white',
            fontWeight: 800,
            lineHeight: 1.05,
            letterSpacing: '-0.5px',
            fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
            maxWidth: 900,
            textShadow: '0 2px 24px rgba(0,0,0,0.4)',
            ...fadeInAnimation
          }}
        >
          {eventData?.title}
        </Typography>

        {eventData?.subtitle && (
          <Typography
            component='p'
            sx={{
              color: 'rgba(255,255,255,0.82)',
              fontWeight: 500,
              mt: 1.5,
              maxWidth: 720,
              fontSize: { xs: '1rem', md: '1.2rem' },
              lineHeight: 1.5
            }}
          >
            {eventData.subtitle}
          </Typography>
        )}

        {count > 1 && (
          <Box sx={{ mt: 3, mb: 3 }}>
            <Button
              onClick={() => openViewer(0)}
              startIcon={<PhotoLibraryOutlinedIcon />}
              sx={{
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.16)',
                backdropFilter: 'blur(8px)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              {t('photos', { n: count })}
            </Button>
          </Box>
        )}
      </Container>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='lg'
        fullWidth
        slotProps={{ paper: { sx: { bgcolor: 'rgba(10,10,10,0.96)', backgroundImage: 'none', borderRadius: 3 } } }}
      >
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: { xs: '60vh', md: '72vh' }, p: { xs: 1, md: 2 } }}>
          <IconButton
            onClick={() => setOpen(false)}
            aria-label={t('close')}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'white', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' } }}
          >
            <CloseIcon />
          </IconButton>

          {count > 1 && (
            <IconButton
              onClick={showPrev}
              aria-label={t('previous')}
              sx={{ position: 'absolute', left: 8, color: 'white', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' } }}
            >
              <ChevronLeftIcon fontSize='large' />
            </IconButton>
          )}

          <Box
            component='img'
            src={images[index]}
            alt={eventData?.title || ''}
            sx={{ maxWidth: '100%', maxHeight: '72vh', objectFit: 'contain', borderRadius: 1.5, display: 'block' }}
          />

          {count > 1 && (
            <IconButton
              onClick={showNext}
              aria-label={t('next')}
              sx={{ position: 'absolute', right: 8, color: 'white', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' } }}
            >
              <ChevronRightIcon fontSize='large' />
            </IconButton>
          )}

          {count > 1 && (
            <Box sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', color: 'white', fontSize: '0.8rem', bgcolor: 'rgba(0,0,0,0.55)', px: 1.5, py: 0.5, borderRadius: 99 }}>
              {index + 1} / {count}
            </Box>
          )}
        </Box>
      </Dialog>
    </Box>
  )
}
